import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdVerifiedUser } from 'react-icons/md';
import {
  AppInput,
  AppSelect,
  AppTextarea,
  InlineWarning,
  OutlineButton,
  PrimaryButton,
  StatusBadge,
} from '../../../components/ui';
import { VERIFICATION_OPTIONS } from '../../../data/master/people';
import { buildVerificationNotifyDefaults } from '../../../models/appUser.model';
import { updateAppUserKyc } from '../../../repositories/appUser.repository';

function badgeStatus(status) {
  if (status === 'Verified') return 'verified';
  if (status === 'Rejected') return 'error';
  return 'pending';
}

function applyNotifyDefaults(role, status, rejectionReason, setTitle, setBody) {
  const next = buildVerificationNotifyDefaults({
    role,
    status,
    rejectionReason,
  });
  setTitle(next.title);
  setBody(next.body);
}

export function AppUserVerificationModal({
  isOpen,
  onClose,
  user,
  onSaved,
  initialRole = null,
}) {
  const toast = useToast();
  const [status, setStatus] = useState('Pending');
  const [role, setRole] = useState('service_provider');
  const [employeeCode, setEmployeeCode] = useState('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyBody, setNotifyBody] = useState('');
  const [notifyError, setNotifyError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !user) return;
    const nextRole =
      initialRole === 'agency' || initialRole === 'service_provider'
        ? initialRole
        : user.userType === 'agency'
          ? 'agency'
          : 'service_provider';
    setRole(nextRole);
    const raw =
      nextRole === 'agency'
        ? user.roles?.agency?.verificationStatus
        : user.roles?.service_provider?.verificationStatus;
    const nextStatus = raw
      ? String(raw).charAt(0).toUpperCase() + String(raw).slice(1)
      : user.verificationStatus || 'Pending';
    setStatus(nextStatus);
    setEmployeeCode(String(user.roles?.agency?.employeeCode || '').trim());
    const nextReason =
      nextStatus === 'Rejected'
        ? (nextRole === 'agency'
            ? user.roles?.agency?.rejectionReason
            : user.roles?.service_provider?.rejectionReason) ||
          user.rejectionReason ||
          ''
        : '';
    setReason(nextReason);
    setReasonError('');
    setNotifyError('');
    applyNotifyDefaults(
      nextRole,
      nextStatus,
      nextReason,
      setNotifyTitle,
      setNotifyBody,
    );
  }, [isOpen, user, initialRole]);

  if (!user) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (status === 'Rejected') {
      const trimmed = reason.trim();
      if (trimmed.length < 5) {
        setReasonError('Enter a clear rejection reason (min 5 characters)');
        return;
      }
      if (trimmed.length > 500) {
        setReasonError('Max 500 characters');
        return;
      }
    }
    if (!notifyTitle.trim()) {
      setNotifyError('Notification title is required');
      return;
    }
    if (!notifyBody.trim()) {
      setNotifyError('Notification message is required');
      return;
    }
    if (notifyTitle.trim().length > 120) {
      setNotifyError('Title max 120 characters');
      return;
    }
    if (notifyBody.trim().length > 1000) {
      setNotifyError('Message max 1000 characters');
      return;
    }

    setSaving(true);
    try {
      await updateAppUserKyc(user.id, {
        verificationStatus: status,
        rejectionReason: status === 'Rejected' ? reason.trim() : null,
        role,
        ...(role === 'agency' ? { employeeCode: employeeCode.trim() } : {}),
        notificationTitle: notifyTitle.trim(),
        notificationBody: notifyBody.trim(),
      });
      toast({
        title: 'Status updated',
        description: `${user.fullName} · ${status} · notification sent`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to update status',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        w={{ base: 'calc(100% - 32px)', md: '720px' }}
        maxW="720px"
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0} pb={2}>
          Update verification status
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
          <VStack align="stretch" spacing={4}>
            <Box
              p={4}
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <Flex align="center" gap={3}>
                <Flex
                  w="44px"
                  h="44px"
                  align="center"
                  justify="center"
                  borderRadius="sm"
                  bg="bg.amberTint"
                  flexShrink={0}
                >
                  <MdVerifiedUser size={22} color="#1A1A1A" />
                </Flex>
                <Box minW={0}>
                  <Text fontWeight="700" fontSize="15px" noOfLines={1}>
                    {user.fullName}
                  </Text>
                  <Flex align="center" gap={2} mt={1} wrap="wrap">
                    <Text fontSize="13px" color="text.secondary">
                      {user.userTypeLabel}
                    </Text>
                    <StatusBadge status={badgeStatus(user.verificationStatus || 'Pending')}>
                      Current: {user.verificationStatus || 'Pending'}
                    </StatusBadge>
                  </Flex>
                </Box>
              </Flex>
            </Box>

            <SimpleGrid columns={{ base: 1, md: role === 'agency' ? 3 : 2 }} spacing={3}>
              <AppSelect
                label="Role"
                isRequired
                value={role}
                onChange={(v) => {
                  setRole(v);
                  const raw =
                    v === 'agency'
                      ? user.roles?.agency?.verificationStatus
                      : user.roles?.service_provider?.verificationStatus;
                  let nextStatus = status;
                  let nextReason = reason;
                  if (raw) {
                    nextStatus =
                      String(raw).charAt(0).toUpperCase() + String(raw).slice(1);
                    setStatus(nextStatus);
                    setReasonError('');
                    if (nextStatus !== 'Rejected') {
                      nextReason = '';
                      setReason('');
                    }
                  }
                  setNotifyError('');
                  applyNotifyDefaults(
                    v,
                    nextStatus,
                    nextReason,
                    setNotifyTitle,
                    setNotifyBody,
                  );
                }}
                options={[
                  { value: 'agency', label: 'Buyer / Agency' },
                  { value: 'service_provider', label: 'Seller / Service provider' },
                ]}
                helperText="Verify each role separately"
              />

              <AppSelect
                label="New status"
                isRequired
                value={status}
                onChange={(v) => {
                  setStatus(v);
                  setReasonError('');
                  const nextReason = v !== 'Rejected' ? '' : reason;
                  if (v !== 'Rejected') setReason('');
                  setNotifyError('');
                  applyNotifyDefaults(
                    role,
                    v,
                    nextReason,
                    setNotifyTitle,
                    setNotifyBody,
                  );
                }}
                options={VERIFICATION_OPTIONS}
                helperText="Verified · Pending · Rejected"
              />

              {role === 'agency' ? (
                <AppInput
                  label="Employee code number"
                  value={employeeCode}
                  onChange={(e) => setEmployeeCode(e.target.value)}
                  placeholder="Optional"
                  maxLength={50}
                  helperText="Optional — buyer / agency profile"
                />
              ) : null}
            </SimpleGrid>

            {status === 'Rejected' ? (
              <Box
                p={4}
                bg="bg.errorTint"
                borderRadius="sm"
                borderWidth="1px"
                borderColor="status.error"
              >
                <Heading size="xs" mb={1} color="status.error">
                  Rejection reason required
                </Heading>
                <Text fontSize="13px" color="text.secondary" mb={4} lineHeight="1.45">
                  Shown on the mobile home and profile screens. Also used in the
                  notification draft below.
                </Text>
                <AppTextarea
                  label="Reason"
                  isRequired
                  value={reason}
                  onChange={(e) => {
                    const next = e.target.value;
                    setReason(next);
                    setReasonError('');
                    setNotifyError('');
                    applyNotifyDefaults(
                      role,
                      status,
                      next,
                      setNotifyTitle,
                      setNotifyBody,
                    );
                  }}
                  placeholder="e.g. Incomplete KYC / address mismatch / invalid documents"
                  rows={3}
                  maxLength={500}
                  isInvalid={!!reasonError}
                  errorMessage={reasonError}
                  helperText={`${reason.trim().length}/500`}
                />
              </Box>
            ) : (
              <InlineWarning>
                {status === 'Verified'
                  ? role === 'agency'
                    ? 'Verified buyers can raise equipment and material requests.'
                    : 'Verified sellers can be matched and recommended for work.'
                  : role === 'agency'
                    ? 'Pending keeps that role’s actions locked until review is complete.'
                    : 'Pending sellers can still add equipment and materials; listings stay under review.'}
              </InlineWarning>
            )}

            <Box
              p={4}
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="bg.input"
            >
              <Heading size="xs" mb={1}>
                Notification to member
              </Heading>
              <Text fontSize="13px" color="text.secondary" mb={3} lineHeight="1.45">
                This message will be sent when you update status. Edit it if needed.
              </Text>
              <VStack align="stretch" spacing={3}>
                <AppInput
                  label="Title"
                  isRequired
                  value={notifyTitle}
                  onChange={(e) => {
                    setNotifyTitle(e.target.value);
                    setNotifyError('');
                  }}
                  maxLength={120}
                  helperText={`${notifyTitle.trim().length}/120`}
                />
                <AppTextarea
                  label="Message"
                  isRequired
                  value={notifyBody}
                  onChange={(e) => {
                    setNotifyBody(e.target.value);
                    setNotifyError('');
                  }}
                  rows={3}
                  maxLength={1000}
                  isInvalid={!!notifyError}
                  errorMessage={notifyError}
                  helperText={`${notifyBody.trim().length}/1000`}
                />
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Update &amp; notify
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
